import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  useTheme,
  Grid2,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useStores } from "../../use-stores";
import DeleteConfirmation from "../components/common/pop-up/DeleteConfirmation";
import ShipmentsTable from "../components/universal-shipment/ShipmentsTable";
import FilterComponent from "../components/universal-shipment/FilterShipmentComponent";
import ShipmentStatusPopup from "../components/universal-shipment/StatusPopup";
import UploadDialog from "../components/common/pop-up/UploadFile";
import { useNotification } from "../../utils/NotificationsContext";
import AddPacksPopup from "../components/universal-shipment/AddPacksPopup";
import CreateShipmentWizardPopup from "../components/universal-shipment/CreateShipmentWizardPopup";
import { ShipmentMethod, Swagpack } from "../../models/Shipments";
import PermissionBlockLabel from "../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../models/Employees/EmployeeRoles";
import RemovePacksConfirmationPopup from "../components/common/pop-up/RemovePacksFromShipment";
import CancelShipmentPopup from "../components/universal-shipment/CancelShipmentPopup";
import { ShipmentStatusEnum } from "../../models/Shipments/ShipmentStatus";
import { FilterValue } from "../../models/Filters/FilterValue";
import CustomTypography from "../components/common/form/CustomTypography";
import AppButton from "../components/common/buttons/AppButton";
import { listingStatusTabsStyles } from "../../styles/listingStatusTabsSx";
import { Dayjs } from "dayjs";
import { GetPacksParams } from "../../stores/ShipmentStore";

const Status = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out For Delivery" },
  { id: 5, value: "Delivered" },
];

const SHIPMENT_METHOD_OPTIONS: ShipmentMethod[] = [
  ShipmentMethod.RAIL,
  ShipmentMethod.SURFACE,
  ShipmentMethod.AIR,
];

const textFieldSx = {
  "& .MuiInputLabel-root": { fontSize: "12px" },
  "& .MuiInputBase-input": { fontSize: "12px" },
};

// Tabs for shipment status filtering
const SHIPMENT_STATUS_TABS = [
  { id: "all", label: "All", statusIds: [] },
  { id: "new", label: "New", statusIds: [ShipmentStatusEnum.NEW] },
  {
    id: "ready",
    label: "Ready To Ship",
    statusIds: [ShipmentStatusEnum.READY_TO_SHIP],
  },
  {
    id: "delayed",
    label: "Delayed Delivery",
    statusIds: [
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.OUT_FOR_DELIVERY,
    ],
  },
  {
    id: "shipped",
    label: "Shipped",
    statusIds: [
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.OUT_FOR_DELIVERY,
    ],
  },
  {
    id: "delivered",
    label: "Delivered",
    statusIds: [ShipmentStatusEnum.DELIVERED],
  },
  {
    id: "rto",
    label: "RTO",
    statusIds: [
      ShipmentStatusEnum.RETURN_REQUESTED,
      ShipmentStatusEnum.RETURNED,
    ],
  },
  {
    id: "failed",
    label: "Failed/Cancelled",
    statusIds: [
      ShipmentStatusEnum.CANCELLED,
      ShipmentStatusEnum.UNDELIVERED,
      ShipmentStatusEnum.DAMAGED,
    ],
  },
];

const getTabById = (tabId?: string | null) =>
  SHIPMENT_STATUS_TABS.find((tab) => tab.id === tabId);

const UniversalShipments: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { shipmentStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();

  const searchParams = new URLSearchParams(location.search);
  let packIds: FilterValue[] = [];
  const packIdsParam = searchParams.get("pack_ids");
  if (packIdsParam) {
    packIds = JSON.parse(packIdsParam) as FilterValue[];
  }
  const initialStatus = searchParams.get("status")?.split(",").map(Number) || [
    ShipmentStatusEnum.NEW,
  ];
  const initialTabIdFromStatus =
    SHIPMENT_STATUS_TABS.find(
      (tab) =>
        tab.statusIds.length === initialStatus.length &&
        tab.statusIds.every((id) => initialStatus.includes(id)),
    )?.id || "new";

  const initialParams = {
    page: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "created_date",
    sortType: (searchParams.get("sort_type") as "ASC" | "DESC") || "DESC",
    search: searchParams.get("search") || "",
    shipment_type: searchParams.get("shipment_type")
      ? searchParams.get("shipment_type")?.split(",").map(Number)
      : [],
    status: initialStatus,
    tab_id:
      getTabById(searchParams.get("tab_id"))?.id || initialTabIdFromStatus,
    delivery_partner_id: searchParams.get("delivery_partner_id")
      ? searchParams.get("delivery_partner_id")?.split(",").map(Number)
      : [],
    order_id: searchParams.get("order_id")
      ? searchParams.get("order_id")?.split(",").map(Number)
      : [],
    dimensions: searchParams.get("dimensions")
      ? searchParams.get("dimensions")?.split(",").map(Number)
      : [],
    c_id: searchParams.get("c_id")
      ? searchParams
          .get("c_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    inventory_type: searchParams.get("inventory_type") || "-1",
    pack_ids: packIds,
    packs_exist:
      searchParams.get("packs_exist") === "true"
        ? true
        : searchParams.get("packs_exist") === "false"
          ? false
          : undefined,
    country: searchParams.get("country")?.split(",") || [],
    search_text_value: searchParams.get("search_text_value")
      ? searchParams
          .get("search_text_value")
          ?.split(",")
          .map((item) => {
            const trimmed = item.trim();
            const num = Number(trimmed);
            return !isNaN(num) && num !== 0 ? num : trimmed;
          })
      : [],
    contact_number: searchParams.get("contact_number") || "",
    allSelected: false,
    start_date: searchParams.get("start_date") || null,
    end_date: searchParams.get("end_date") || null,
  };

  const [params, setParams] = useState(initialParams);
  // const [selectedShipmentIds, setSelectedShipmentIds] = useState<number[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [returnedId, setReturnedId] = useState<number | null>(null);
  const [removePacksFromShipmentId, setRemovePacksFromShipmentId] = useState<
    number | null
  >(null);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [clearShipments, setClearShipments] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreateShipmentWizardOpen, setIsCreateShipmentWizardOpen] =
    useState(false);
  const [isEditCsvDialogOpen, setIsEditCsvDialogOpen] = useState(false);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [bulkEditFields, setBulkEditFields] = useState({
    length: "",
    breadth: "",
    height: "",
    weight: "",
    delivery_partner_id: -1,
    shipment_method: "" as ShipmentMethod | "",
  });
  const [addPacksParams, setAddPacksParams] = useState<GetPacksParams | null>(
    null,
  );
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const activeStatusTab = React.useMemo(
    () => getTabById(params.tab_id)?.id || SHIPMENT_STATUS_TABS[0].id,
    [params.tab_id],
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleStatusTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    // Cancel in-flight API request when tab changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const selectedTab = getTabById(newValue) || SHIPMENT_STATUS_TABS[0];
    setParams((prev) => ({
      ...prev,
      tab_id: selectedTab.id,
      status: selectedTab.statusIds,
      page: 1,
    }));
  };

  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());

  const fetchShipments = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const res = await shipmentStore.getShipments(
        params,
        false,
        controller.signal,
      );
      if (!res && shipmentStore.error) {
        showError("Error", shipmentStore.error as string);
      }
    } catch (error: any) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
        return;
      }
      showError("Error", "Error fetching shipments");
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [params, shipmentStore, showError]);

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_SHIPMENTS)) {
      fetchShipments();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, shipmentStore]);

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_SHIPMENTS)) {
      shipmentStore.getCountByStatus();
    }
  }, [userStore, shipmentStore]);

  useEffect(() => {
    setAllSelected(false);

    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (key !== "emails") {
          if (value && (!Array.isArray(value) || value.length > 0)) {
            const paramValue = Array.isArray(value)
              ? value.join(",")
              : value.toString();
            searchParams.set(key, paramValue);
          }
        }

        if (key === "c_id") {
          const paramValue = params.c_id
            .map((item) => item.id + ":" + item.value)
            .join(",");
          searchParams.set(key, paramValue);
        }

        if (key === "packs_exist") {
          if (params.packs_exist !== undefined) {
            searchParams.set(key, params.packs_exist ? "true" : "false");
          }
        }

        if (key === "country") {
          searchParams.set(key, params.country.join(","));
        }

        if (key === "pack_ids") {
          if (value && (!Array.isArray(value) || value.length > 0)) {
            searchParams.set(key, JSON.stringify(value));
          }
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
  }, [params, navigate, shipmentStore]);

  const handleFilterChange = useCallback(
    (filters: {
      shipmentType: number[];
      deliveryPartner: number[];
      orderIds: number[];
      dimensions: number[];
      emails?: string;
      inventoryType: string;
      packIds: FilterValue[];
      packs_exist?: boolean | undefined;
      country?: string[] | undefined;
      search_text_value?: (number | string)[] | undefined;
      contact_number?: string | undefined;
      customers: FilterValue[];
      start_date: Dayjs | null;
      end_date: Dayjs | null;
    }) => {
      if (filters.emails && filters.emails !== "") {
        const emails_list = filters.emails.split(",");
        if (emails_list.length > 200) {
          showError("Error", "200 is the limit of email ids for filters.");
          return;
        }
      }

      // Cancel in-flight request when filters change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setParams((prev) => ({
        ...prev,
        shipment_type: filters.shipmentType,
        delivery_partner_id: filters.deliveryPartner,
        order_id: filters.orderIds,
        dimensions: filters.dimensions,
        emails: filters.emails,
        inventory_type: filters.inventoryType,
        pack_ids: filters.packIds,
        packs_exist: filters.packs_exist,
        country: filters.country || [],
        search_text_value: filters.search_text_value || [],
        contact_number: filters.contact_number || "",
        c_id: filters.customers || [],
        start_date: filters.start_date?.format("YYYY-MM-DD") || null,
        end_date: filters.end_date?.format("YYYY-MM-DD") || null,
        page: 1,
      }));
      // setSelectedShipmentIds([]);
      setSelectedShipmentIdsMap(new Map());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSortChange = (column: string) => {
    const newSortType = params.sortType === "ASC" ? "DESC" : "ASC";
    setParams((prev) => ({
      ...prev,
      sortBy: column,
      sortType: newSortType,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const toggleAllSelected = (selected: boolean) => {
    setAllSelected(selected);
  };

  const handleCancelShipmentConfirm = async (
    shipmentId: number,
    reason: string,
  ) => {
    if (shipmentId) {
      const res = await shipmentStore.deleteShipment(
        shipmentId,
        ShipmentStatusEnum.CANCELLED,
        reason,
      );
      setDeleteId(null);
      if (res?.success) {
        showSuccess(res?.result);
      } else {
        showError(
          "Error Occured",
          shipmentStore.error || "Error cancelling shipment",
        );
      }
    }
  };

  const handleReturnedConfirm = async (shipmentId: number) => {
    if (shipmentId) {
      const res = await shipmentStore.deleteShipment(
        shipmentId,
        ShipmentStatusEnum.RETURNED,
      );
      setReturnedId(null);
      if (res?.success) {
        showSuccess(res?.result);
      } else {
        showError(
          "Error Occured",
          shipmentStore.error || "Error deleting shipment",
        );
      }
    }
  };

  const handleRemovePacksConfirm = async (shipmentId: number) => {
    if (shipmentId) {
      const res = await shipmentStore.removePacksFromShipment(shipmentId);
      setRemovePacksFromShipmentId(null);
      if (res?.success) {
        showSuccess(res?.result);
      } else {
        showError(
          "Error Occured",
          shipmentStore.error ||
            "Failed to remove packs from shipment, please try again later",
        );
      }
    }
  };

  const handleUpdateShipmentStatus = async (
    shipmentId: number,
    status: ShipmentStatusEnum,
  ) => {
    if (status !== ShipmentStatusEnum.NEW) {
      return;
    }
    const res = await shipmentStore.moveShipmentToNew(shipmentId);
    if (res?.success) {
      await fetchShipments();
      shipmentStore.getCountByStatus();
    } else {
      showError(
        "Error",
        shipmentStore.error ||
          res?.message ||
          "Failed to move shipment to New. Please try again later.",
      );
    }
  };

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_SHIPMENTS)) {
      shipmentStore.getDeliveryPartner(undefined, true, false);
    }
  }, [shipmentStore, userStore]);

  useEffect(() => {
    if (clearShipments) {
      setClearShipments(false);
    }
  }, [clearShipments]);

  const downloadCsv = async () => {
    try {
      if (allSelected === true) {
        const response =
          await shipmentStore.downloadBulkShipmentsCSVReport(params);
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
        showSuccess(response.message);
      } else {
        await shipmentStore.getShipments(params, true);
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
      }
    } catch (error: any) {
      showError("Error Occured", error?.message || "Error downloading CSV");
    }
  };

  const downloadShiprocketCsv = async () => {
    try {
      if (allSelected === true) {
        const response = await shipmentStore.getShiprocketCSVBulkReport(params);
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
        showSuccess(response.message);
      } else {
        await shipmentStore.downloadShiprocketCSV(params);
      }
    } catch (error: any) {
      console.log(error);
      showError(
        "Error Occured",
        error?.response?.data?.message ||
          error?.message ||
          "Error downloading CSV",
      );
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBulkMenu = () => {
    if (actionsAnchorRef.current) {
      setAnchorEl(actionsAnchorRef.current);
    }
  };

  const handleDialogOpen = () => {
    setIsEditCsvDialogOpen(true);
  };

  const handleFileUpload = async (file: File | null) => {
    if (file) {
      const res = await shipmentStore.uploadCsvForBulkEdit(file);
      if (res?.success) {
        showSuccess(res?.result);
        setParams((prevParams) => ({
          ...prevParams,
          page: 1,
        }));
      } else {
        showError("Error Occured", shipmentStore.error || "File Upload Failed");
      }
    }
    setIsEditCsvDialogOpen(false);
  };

  const handleProcessShipments = async () => {
    const res = await shipmentStore.processShipments(
      Array.from(selectedShipmentIdsMap.keys()),
    );
    if (res?.success) {
      showSuccess(res?.result);
      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
      // setSelectedShipmentIds([]);
      // setSelectedShipmentIdsMap(new Map());
    } else {
      // setSelectedShipmentIds([]);
      showError(
        "Error Occured",
        shipmentStore?.error || "Something went wrong",
      );
    }
    setClearShipments(true);
  };

  const resetBulkEditFields = () => {
    setBulkEditFields({
      length: "",
      breadth: "",
      height: "",
      weight: "",
      delivery_partner_id: -1,
      shipment_method: "",
    });
  };

  const handleBulkUpdate = async ({
    dimensions,
    weight,
    status,
    deliveryPartnerId,
    shipmentMethod,
  }: {
    dimensions?: { length: number; breadth: number; height: number };
    weight?: number;
    status?: number;
    deliveryPartnerId?: number;
    shipmentMethod?: ShipmentMethod;
  }) => {
    const selectedIds = Array.from(selectedShipmentIdsMap.keys());
    const res = await shipmentStore.bulkShipmentUpdate(
      { ...params, allSelected: allSelected },
      selectedIds,
      dimensions?.length,
      dimensions?.breadth,
      dimensions?.height,
      weight,
      status,
      deliveryPartnerId,
      shipmentMethod,
    );

    if (res?.success) {
      setAllSelected(false);
      setClearShipments(true);
      // setSelectedShipmentIds([]);
      setActivePopup(null);
      // setSelectedShipmentIdsMap(new Map());
      showSuccess(res?.result || "Saved successfully");
    } else {
      setClearShipments(true);
      // setSelectedShipmentIds([]);
      showError("Error Occurred", shipmentStore.error || "Error saving data");
    }
  };

  const handlePopupSubmit = (type: string, value: any) => {
    if (type === "status") {
      return handleBulkUpdate({ status: value });
    }
  };

  const handleBulkEditSubmit = async () => {
    const {
      length,
      breadth,
      height,
      weight,
      delivery_partner_id,
      shipment_method,
    } = bulkEditFields;
    if (
      !length ||
      !breadth ||
      !height ||
      !weight ||
      delivery_partner_id === -1 ||
      !shipment_method
    ) {
      showError("Error", "Please fill all fields");
      return;
    }

    await handleBulkUpdate({
      dimensions: {
        length: Number(length),
        breadth: Number(breadth),
        height: Number(height),
      },
      weight: Number(weight),
      deliveryPartnerId: delivery_partner_id,
      shipmentMethod: shipment_method,
    });
  };

  const openAddPacksPopup = (shipmentIdsMap: Map<number, number>) => {
    const shipments = shipmentStore.shipments?.filter((shipment) =>
      shipmentIdsMap.has(shipment.id),
    );

    const shipmentCampaignIdMap = new Map<number, number>();
    shipments?.forEach((shipment) => {
      if (shipment.id && shipment.campaign_id) {
        shipmentCampaignIdMap.set(shipment.id, shipment.campaign_id);
      }
    });

    const selectedCampaignIds = Array.from(shipmentCampaignIdMap.keys())
      .map((id) => shipmentCampaignIdMap.get(id))
      .filter((id): id is number => id !== undefined);

    const uniqueCampaignIds = new Set(selectedCampaignIds);

    if (uniqueCampaignIds.size > 0) {
      if (uniqueCampaignIds.size > 1) {
        showError("Error", "Select shipments with the same Campaign Id");
        return;
      }
    } else {
      const shipmentInventoryMap = new Map<number, number>();
      shipments?.forEach((shipment) => {
        if (shipment.id && shipment.inventory_id) {
          shipmentInventoryMap.set(shipment.id, shipment.inventory_id);
        }
      });

      const selectedInventoryIds = Array.from(shipmentIdsMap.keys())
        .map((id) => shipmentInventoryMap.get(id))
        .filter((id): id is number => id !== undefined);

      const uniqueInventoryIds = new Set(selectedInventoryIds);

      if (uniqueInventoryIds.size > 1) {
        showError("Error", "Select shipments with the same Inventory Id");
        return;
      }
      const inventoryId = Array.from(uniqueInventoryIds)[0];
      if (!inventoryId && (shipments?.length ?? 0) > 0) {
        showError(
          "Error",
          "Shipment does not have an inventory or campaign assigned.",
        );
        return;
      }
    }

    const getPacksParams: GetPacksParams = {
      shipment_ids: Array.from(shipmentIdsMap.keys()),
    };
    setAddPacksParams(getPacksParams);
    setActivePopup("addPacks");
  };

  const handleAddPacksForShipment = (shipment_id: number) => {
    const getPacksParams: GetPacksParams = {
      shipment_ids: [shipment_id],
    };
    const map = new Map();
    map.set(shipment_id, shipment_id);
    setSelectedShipmentIdsMap(map);

    setAddPacksParams(getPacksParams);
    setActivePopup("addPacks");
  };

  const handleBulkActions = (action: string | null) => {
    handleClose();
    if (action === "addPacks") {
      openAddPacksPopup(selectedShipmentIdsMap);
    } else if (action === "bulkEditShipments") {
      setActivePopup("bulkEditShipments");
    } else {
      setActivePopup(action);
    }
  };

  const handleClosePopup = () => {
    if (activePopup === "addPacks") {
      if (selectedShipmentIdsMap.size === 1) {
        setSelectedShipmentIdsMap(new Map());
      }
      setAddPacksParams(null);
    }
    if (activePopup === "bulkEditShipments") {
      resetBulkEditFields();
    }
    setActivePopup(null);
  };

  const handleSavePacks = async (packs: Swagpack) => {
    const selectedIds = Array.from(selectedShipmentIdsMap.keys());

    const res = await shipmentStore.savePacks(selectedIds, packs);
    if (res?.success) {
      showSuccess(res?.result);
      setClearShipments(true);
      // setSelectedShipmentIds([]);
      setActivePopup(null);
      // setSelectedShipmentIdsMap(new Map());
      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
    } else {
      showError("Error Occured", shipmentStore.error || "Error adding pack");
    }
  };

  // const handleShipmentsDownloadTemplate = async (filename: string) => {
  //   const url =
  //     "https://procureasystorage.s3.ap-south-1.amazonaws.com/templates/CSV_Shipping_Template.csv";

  //   try {
  //     try {
  //       const response = await fetch(url);
  //       if (!response.ok) {
  //         throw new Error(`HTTP error! status: ${response.status}`);
  //       }

  //       const blob = await response.blob();
  //       const downloadUrl = window.URL.createObjectURL(blob);

  //       const a = document.createElement("a");
  //       a.href = downloadUrl;
  //       a.download = filename || "CSV_Shipping_Template.csv";
  //       a.style.display = "none";
  //       document.body.appendChild(a);
  //       a.click();
  //       document.body.removeChild(a);

  //       window.URL.revokeObjectURL(downloadUrl);
  //     } catch (fetchError) {
  //       console.log("Fetch failed, trying window.open approach:", fetchError);
  //       window.open(url, "_blank");
  //     }
  //   } catch (error: any) {
  //     console.error("Download error:", error);
  //     showError(
  //       "Error",
  //       error?.message || "Failed to download CSV_Shipping_Template",
  //     );
  //   }
  // };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {userStore.hasPermission(EmployeeRoles.VIEW_SHIPMENTS) ? (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <CustomTypography variant="heading3">
                  Shipments
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors?.greyTextColor}
                >
                  Manage shipments, apply bulk actions, and export shipment
                  data.
                </CustomTypography>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {userStore.hasPermission(EmployeeRoles.CREATE_SHIPMENTS) && (
                  <Box>
                    <AppButton
                      variant="primary"
                      label="Add Shipments"
                      onClick={() => setIsCreateShipmentWizardOpen(true)}
                    />
                  </Box>
                )}
                {userStore.hasPermission(EmployeeRoles.EDIT_SHIPMENTS) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      gap: 1,
                    }}
                  >
                    <Box ref={actionsAnchorRef}>
                      <AppButton
                        variant="grayscale"
                        label={`Actions${
                          selectedShipmentIdsMap.size > 0 || allSelected
                            ? ` (${
                                allSelected
                                  ? shipmentStore.totalCount
                                  : selectedShipmentIdsMap.size
                              })`
                            : ""
                        }`}
                        onClick={handleBulkMenu}
                        customStyles={{ minWidth: 180 }}
                      />
                    </Box>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      disableAutoFocusItem
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow:
                              "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                            minWidth: 240,
                            overflow: "hidden",
                          },
                        },
                      }}
                    >
                      <>
                        <MenuItem
                          onClick={() => handleBulkActions("bulkEditShipments")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Update Dimensions, Weight & Partner
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleBulkActions("addStatus")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Status
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleBulkActions("addPacks")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Pack
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleDialogOpen()}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Tracking Details
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleProcessShipments()}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Process Shipments
                          </CustomTypography>
                        </MenuItem>
                      </>

                      {userStore.hasPermission(
                        EmployeeRoles.EDIT_SHIPMENTS,
                        true,
                      ) && (
                        <MenuItem
                          onClick={downloadCsv}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Download Filtered Data CSV
                          </CustomTypography>
                        </MenuItem>
                      )}
                      {userStore.hasPermission(
                        EmployeeRoles.EDIT_SHIPMENTS,
                        true,
                      ) && (
                        <MenuItem
                          onClick={downloadShiprocketCsv}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Download Shiprocket CSV
                          </CustomTypography>
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                )}
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isFiltersExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading7">
                  Search & Filters
                </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFiltersExpanded(!isFiltersExpanded);
                  }}
                >
                  {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <FilterComponent
                    onFilterChange={handleFilterChange}
                    deliveryPartners={shipmentStore?.deliveryPartners}
                  />
                </Box>
              </Collapse>
            </Paper>

            {shipmentStore.countByStatus?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 1,
                  py: 0.75,
                  px: 1,
                  fontSize: "0.75rem",
                }}
              >
                <CustomTypography variant="body2" fontWeight={500}>
                  Today's Count:
                </CustomTypography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {shipmentStore.countByStatus.map((item, idx) => {
                    return (
                      <React.Fragment key={item.tab_id}>
                        {idx > 0 && (
                          <Box
                            component="span"
                            sx={{ color: theme.palette.divider, mx: 0.25 }}
                          >
                            ·
                          </Box>
                        )}
                        <Box sx={{ display: "inline-flex" }}>
                          <CustomTypography variant="body3" fontWeight={500}>
                            {item.label.toUpperCase()}:{" "}
                          </CustomTypography>
                          <CustomTypography variant="body1" fontWeight={600}>
                            {item.requests_count}
                          </CustomTypography>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Tabs
              value={activeStatusTab}
              onChange={handleStatusTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={(t) => ({ ...listingStatusTabsStyles(t), mt: 1 })}
            >
              {SHIPMENT_STATUS_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.label.toUpperCase()}
                  id={`shipment-tab-${tab.id}`}
                  value={tab.id}
                />
              ))}
            </Tabs>

            {shipmentStore.loading ? (
              <Box
                sx={{
                  minHeight: "40vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* <Box sx={{ display: "flex", mt: 2 }}>
                  <Checkbox
                    color="primary"
                    indeterminate={false}
                    checked={allSelected}
                    onClick={() => {
                      toggleAllSelected(!allSelected);
                      setSelectedShipmentIdsMap(new Map());
                    }}
                    sx={{ padding: 0, marginLeft: "24px", marginRight: "10px" }}
                  />
                  <CustomTypography variant="heading7">
                    {allSelected
                      ? shipmentStore.totalCount + " selected."
                      : shipmentStore.totalCount}
                  </CustomTypography>
                </Box> */}
                <ShipmentsTable
                  list={shipmentStore.shipments || []}
                  limit={params.limit}
                  page_no={params.page}
                  total_count={shipmentStore.totalCount}
                  total_pages={shipmentStore.totalPages}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onDelete={(id) => {
                    setDeleteId(id);
                  }}
                  onReturned={(id) => {
                    setReturnedId(id);
                  }}
                  onRemovePacks={(id) => {
                    setRemovePacksFromShipmentId(id);
                  }}
                  onAddPacks={handleAddPacksForShipment}
                  allSelected={allSelected}
                  deliveryPartners={shipmentStore?.deliveryPartners}
                  // onSelectedShipmentsChange={setSelectedShipmentIds}
                  clearShipments={clearShipments}
                  onSortChange={handleSortChange}
                  orderBy={params.sortBy}
                  orderDirection={params.sortType}
                  selectedShipmentIdsMap={selectedShipmentIdsMap}
                  selectAllItem={() => {
                    const map = new Map<number, number>();
                    shipmentStore.shipments?.forEach((item) =>
                      map.set(item.id, item.id),
                    );
                    toggleAllSelected(false);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectAllItem={() => {
                    toggleAllSelected(false);
                    setSelectedShipmentIdsMap(new Map());
                  }}
                  selectItem={(item: any) => {
                    toggleAllSelected(false);

                    const map = new Map(selectedShipmentIdsMap);
                    map.set(item.id, item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectItem={(item: any) => {
                    toggleAllSelected(false);

                    const map = new Map(selectedShipmentIdsMap);
                    map.delete(item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  updateShipmentStatus={(id, status) =>
                    handleUpdateShipmentStatus(id, status)
                  }
                />
                {deleteId !== null && (
                  <CancelShipmentPopup
                    open={true}
                    shipmentId={deleteId}
                    onConfirm={handleCancelShipmentConfirm}
                    onCancel={() => setDeleteId(null)}
                  />
                )}

                {returnedId !== null && (
                  <DeleteConfirmation
                    id={returnedId}
                    open={true}
                    onDeleteConfirm={() => handleReturnedConfirm(returnedId)}
                    onCancel={() => setReturnedId(null)}
                    title="Confirm"
                    content="Are you sure you want to mark this shipment as RETURNED?"
                  />
                )}

                {removePacksFromShipmentId !== null && (
                  <RemovePacksConfirmationPopup
                    id={removePacksFromShipmentId}
                    open={true}
                    onRemovePacksConfirm={() =>
                      handleRemovePacksConfirm(removePacksFromShipmentId)
                    }
                    onCancel={() => setRemovePacksFromShipmentId(null)}
                    title="Confirm remove"
                    content="Are you sure you want to remove packs from this shipment?"
                  />
                )}

                {activePopup === "bulkEditShipments" && (
                  <Dialog
                    open={true}
                    onClose={handleClosePopup}
                    maxWidth="md"
                    fullWidth
                    slotProps={{
                      transition: {
                        timeout: 300,
                        sx: {
                          backgroundColor: theme.palette.customColors?.backdrop,
                          backdropFilter: "blur(4px)",
                        },
                      },
                      backdrop: {
                        timeout: 300,
                        sx: {
                          backgroundColor: theme.palette.customColors?.backdrop,
                          backdropFilter: "blur(4px)",
                        },
                      },
                      paper: {
                        sx: {
                          borderRadius: "16px",
                          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                          overflow: "hidden",
                          maxHeight: "90vh",
                          background: `linear-gradient(135deg, ${theme.palette.customColors?.white || "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor || "#f5f5f5"} 100%)`,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
                        borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
                      }}
                    >
                      <CustomTypography variant="heading4">
                        Bulk Update Shipment Fields
                      </CustomTypography>
                      <IconButton
                        onClick={handleClosePopup}
                        sx={{
                          color:
                            theme.palette.customColors?.greyTextColor || "#666",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightGreyColor,
                            color: theme.palette.customColors?.blackBorder,
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <DialogContent
                      sx={{
                        padding: { xs: "20px", sm: "24px" },
                        "&::-webkit-scrollbar": { width: "8px" },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor:
                            theme.palette.customColors?.greyBackground,
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor:
                            theme.palette.customColors?.greyDarkborder,
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.greyTextColor,
                          },
                        },
                      }}
                    >
                      <Box sx={{ mb: 3 }}>
                        <CustomTypography
                          variant="heading6"
                          sx={{ fontWeight: 600 }}
                          style={{ marginBottom: "16px" }}
                        >
                          Package Details
                        </CustomTypography>
                        <Grid2
                          container
                          spacing={2}
                          columns={{ xs: 12, sm: 12, md: 12 }}
                        >
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                              size="small"
                              label="Length"
                              type="number"
                              value={bulkEditFields.length}
                              onChange={(e) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  length: e.target.value,
                                }))
                              }
                              sx={textFieldSx}
                              fullWidth
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                              size="small"
                              label="Breadth"
                              type="number"
                              value={bulkEditFields.breadth}
                              onChange={(e) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  breadth: e.target.value,
                                }))
                              }
                              sx={textFieldSx}
                              fullWidth
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                              size="small"
                              label="Height"
                              type="number"
                              value={bulkEditFields.height}
                              onChange={(e) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  height: e.target.value,
                                }))
                              }
                              sx={textFieldSx}
                              fullWidth
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                              size="small"
                              label="Weight"
                              type="number"
                              value={bulkEditFields.weight}
                              onChange={(e) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  weight: e.target.value,
                                }))
                              }
                              sx={textFieldSx}
                              fullWidth
                            />
                          </Grid2>
                        </Grid2>
                      </Box>

                      <Box
                        sx={{
                          my: 3,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      />

                      <Box sx={{ mb: 1 }}>
                        <CustomTypography
                          variant="heading6"
                          sx={{ fontWeight: 600 }}
                          style={{ marginBottom: "16px" }}
                        >
                          Partner Details
                        </CustomTypography>
                        <Grid2
                          container
                          spacing={2}
                          columns={{ xs: 12, sm: 12, md: 12 }}
                        >
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl
                              fullWidth
                              size="small"
                              sx={textFieldSx}
                            >
                              <InputLabel id="shipment-method-select-label">
                                Shipment Method
                              </InputLabel>
                              <Select
                                labelId="shipment-method-select-label"
                                label="Shipment Method"
                                value={bulkEditFields.shipment_method}
                                onChange={(e) =>
                                  setBulkEditFields((prev) => ({
                                    ...prev,
                                    shipment_method: e.target
                                      .value as ShipmentMethod,
                                  }))
                                }
                              >
                                <MenuItem value="">None</MenuItem>
                                {SHIPMENT_METHOD_OPTIONS.map((method) => (
                                  <MenuItem key={method} value={method}>
                                    {method}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl
                              fullWidth
                              size="small"
                              sx={textFieldSx}
                            >
                              <InputLabel id="delivery-partner-select-label">
                                Delivery Partner
                              </InputLabel>
                              <Select
                                labelId="delivery-partner-select-label"
                                label="Delivery Partner"
                                value={bulkEditFields.delivery_partner_id}
                                onChange={(e) =>
                                  setBulkEditFields((prev) => ({
                                    ...prev,
                                    delivery_partner_id: Number(e.target.value),
                                  }))
                                }
                              >
                                <MenuItem value={-1}>None</MenuItem>
                                {shipmentStore?.deliveryPartners?.map(
                                  (partner) => (
                                    <MenuItem
                                      key={partner.delivery_partner_id}
                                      value={partner.delivery_partner_id}
                                    >
                                      {partner.delivery_partner_name}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                            </FormControl>
                          </Grid2>
                        </Grid2>
                      </Box>
                    </DialogContent>
                    <DialogActions
                      sx={{
                        padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
                        borderTop: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor,
                        gap: 2,
                      }}
                    >
                      <Button
                        onClick={handleClosePopup}
                        variant="outlined"
                        sx={{
                          borderColor:
                            theme.palette.customColors?.greyDarkborder,
                          color: theme.palette.customColors?.greyTextColor,
                          fontWeight: 600,
                          padding: "10px 24px",
                          borderRadius: "8px",
                          "&:hover": {
                            borderColor:
                              theme.palette.customColors?.greyTextColor,
                            backgroundColor:
                              theme.palette.customColors?.lightGreyColor,
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBulkEditSubmit}
                        sx={{
                          backgroundColor:
                            theme.palette.customColors?.EmeraldGreen ||
                            theme.palette.primary.main,
                          color: theme.palette.customColors?.white || "#fff",
                          fontWeight: 600,
                          padding: "10px 24px",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.greenDark ||
                              theme.palette.primary.dark,
                            transform: "translateY(-1px)",
                            boxShadow: `0 4px 12px ${theme.palette.customColors?.EmeraldGreen || theme.palette.primary.main}30`,
                          },
                        }}
                      >
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}

                {activePopup === "addStatus" && (
                  <ShipmentStatusPopup
                    open={true}
                    data={Status}
                    onClose={handleClosePopup}
                    onStatusDropdownSelect={(value) =>
                      handlePopupSubmit("status", value)
                    }
                  />
                )}

                {activePopup === "addPacks" && addPacksParams && (
                  <AddPacksPopup
                    open={true}
                    getPacksParams={addPacksParams}
                    onClose={handleClosePopup}
                    onSavePacks={handleSavePacks}
                  />
                )}

                <CreateShipmentWizardPopup
                  open={isCreateShipmentWizardOpen}
                  onClose={() => setIsCreateShipmentWizardOpen(false)}
                  onSuccess={() =>
                    setParams((prev) => ({
                      ...prev,
                      status: SHIPMENT_STATUS_TABS.find((t) => t.id === "new")
                        ?.statusIds ?? [ShipmentStatusEnum.NEW],
                      page: 1,
                    }))
                  }
                />

                <UploadDialog
                  open={isEditCsvDialogOpen}
                  dialogTitle="Upload File"
                  onClose={() => setIsEditCsvDialogOpen(false)}
                  onUpload={handleFileUpload}
                />
              </>
            )}
          </Box>
        </Paper>
      ) : (
        <PermissionBlockLabel />
      )}
    </Grid2>
  );
};

export default observer(UniversalShipments);
